<script lang="ts">
	import Fa from 'svelte-fa';
	import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
	// SvelteEvent removed in Skeleton 4 — local stub until 02-05a rewire
	type SvelteEvent<E, T> = E & { currentTarget: EventTarget & T };

	interface Props {
		name: string;
		checked?: boolean;
		variant?: 'default' | 'primary' | 'surface' | 'power' | 'custom';
		size?: 'sm' | 'md' | 'lg';
		mode?: 'icon-only' | 'text-only' | 'combined';
		icon?: IconDefinition | undefined;
		iconUnchecked?: IconDefinition | undefined;
		iconClasses?: string;
		label?: string;
		disabled?: boolean;
		classes?: string;
		customBackground?: string;
		customActive?: string;
		title?: string;
		onkeyup?: (event: SvelteEvent<KeyboardEvent, HTMLDivElement>) => void;
		onchange?: (event: Event) => void;
		children?: import('svelte').Snippet;
	}

	let {
		name,
		checked = $bindable(false),
		variant = 'default',
		size = 'sm',
		mode = 'text-only',
		icon = undefined,
		iconUnchecked = undefined,
		iconClasses = '',
		label = '',
		disabled = false,
		classes = '',
		customBackground = '',
		customActive = '',
		title = '',
		onkeyup,
		onchange,
		children
	}: Props = $props();

	const variants = {
		default: {
			background:
				'bg-gradient-to-b from-gray-400/30 to-gray-600/30 dark:bg-gradient-to-b dark:from-gray-400/30 dark:to-gray-600/30',
			active: 'bg-primary-500 dark:bg-primary-500'
		},
		primary: {
			background: 'bg-primary-100 dark:bg-primary-800',
			active: 'bg-primary-500'
		},
		surface: {
			background: 'bg-surface-100 dark:bg-surface-800',
			active: 'bg-surface-600'
		},
		power: {
			background: 'bg-error-500 dark:bg-error-500',
			active: 'bg-success-500 dark:bg-success-500'
		}
	};

	let currentVariant = $derived(
		variant == 'custom' ? { background: customBackground, active: customActive } : variants[variant]
	);

	// CR-01: Must be $derived so trackSize tracks `size` prop changes after first
	// render. The previous top-level `switch` ran exactly once at script init under
	// runes mode (Svelte 4's reactive-init re-run no longer applies), freezing
	// trackSize on the first-seen size.
	let trackSize = $derived.by(() => {
		switch (size) {
			case 'sm':
				return 'w-12 h-6';
			case 'lg':
				return 'w-20 h-10';
			default:
				return 'w-16 h-8';
		}
	});

	// svelte-fa ^4 dropped `"md"` from `IconSize`; the default (undefined size prop)
	// is the equivalent. Map our local `'sm'|'md'|'lg'` Toggle size onto svelte-fa's
	// `IconSize | undefined` shape.
	let iconSize = $derived(size === 'md' ? undefined : size);

	const cBase = 'inline-block';
	const cLabel = 'unstyled flex items-center';
	const cTrack = 'relative transition-all duration-[200ms] cursor-pointer';
	const cThumb =
		'absolute top-0.5 aspect-square h-[calc(100%-4px)] transition-all duration-[200ms] shadow';
	const cThumbIcon = 'h-full w-full self-center text-black dark:text-black';

	function onKeyDown(event: SvelteEvent<KeyboardEvent, HTMLDivElement>) {
		if (['Enter', 'Space'].includes(event.code)) {
			event.preventDefault();
			onkeyup?.(event);
			const inputElem = event.currentTarget.firstChild as HTMLLabelElement;
			inputElem.click();
		}
	}

	let cTrackActive = $derived(
		checked ? currentVariant.active : `${currentVariant.background} cursor-pointer`
	);
	const cThumbBackground = 'bg-white';
	let cThumbPos = $derived(checked ? 'translate-x-[calc(100%+0.375rem)]' : 'translate-x-0.5');

	let classesDisabled = $derived(
		disabled ? 'opacity-50' : 'hover:brightness-[105%] dark:hover:brightness-110 cursor-pointer'
	);
	let classesBase = $derived(`${cBase} rounded-full ${classesDisabled} ${classes}`);
	let classesLabel = $derived(`${cLabel}`);
	let classesTrack = $derived(`${cTrack} rounded-full ${trackSize} ${cTrackActive}`);
	let classesThumb = $derived(`${cThumb} rounded-full ${cThumbBackground} ${cThumbPos}`);
	let classesThumbIcon = $derived(`${cThumbIcon} ${iconClasses}`);
</script>

{#if mode === 'icon-only'}
	<div
		class="slide-toggle {classesBase}"
		data-testid="slide-toggle"
		onkeydown={onKeyDown}
		role="switch"
		aria-label={label || name}
		aria-checked={checked}
		tabindex="0"
		{title}
	>
		<label class="slide-toggle-label {classesLabel}">
			<input
				type="checkbox"
				class="slide-toggle-input hidden"
				bind:checked
				{name}
				{onchange}
				{disabled}
			/>
			<div class="slide-toggle-track {classesTrack}" class:cursor-not-allowed={disabled}>
				<div class="slide-toggle-thumb {classesThumb}" class:cursor-not-allowed={disabled}>
					<div class="flex h-full w-full">
						{#if iconUnchecked && checked && icon}
							<Fa size={iconSize} {icon} class={classesThumbIcon} />
						{:else if iconUnchecked && !checked}
							<Fa size={iconSize} icon={iconUnchecked} class={classesThumbIcon} />
						{:else if icon}
							<Fa size={iconSize} {icon} class={classesThumbIcon} />
						{/if}
					</div>
				</div>
			</div>
		</label>
	</div>
{:else}
	<!-- Skel 4 <Switch> Root anatomy drops flat props (background/active/size) and
	     renders empty (02-06 fixup, Rule 1). Hand-roll the same slide-toggle as
	     ThemeToggle uses: track + absolutely-positioned thumb that translates
	     between the two end positions. -->
	<label class="inline-flex items-center gap-2 {classesDisabled} {classes}" {title}>
		<input type="checkbox" class="hidden" bind:checked {name} {onchange} {disabled} />
		<span
			class="relative inline-block {trackSize} {checked
				? currentVariant.active
				: currentVariant.background} rounded-full transition-colors"
		>
			<span
				class="absolute top-0.5 aspect-square h-[calc(100%-4px)] rounded-full bg-white shadow transition-transform duration-200 {checked
					? 'translate-x-[calc(100%+0.375rem)]'
					: 'translate-x-0.5'}"
			></span>
		</span>
		<span>{label || ''}{@render children?.()}</span>
	</label>
{/if}
